import { Button as AriaButton } from 'react-aria-components'
import type { ButtonProps as AriaButtonProps } from 'react-aria-components'
import { cx } from '../../cx'

export type ButtonVariant = 'solid' | 'outline' | 'ghost'
export type ButtonSize = 'sm' | 'md' | 'lg'
export type ButtonTone = 'accent' | 'neutral' | 'warn'

export interface ButtonProps extends Omit<AriaButtonProps, 'className' | 'style'> {
  variant?: ButtonVariant
  size?: ButtonSize
  tone?: ButtonTone
  fullWidth?: boolean
  className?: string
  style?: React.CSSProperties
}

/**
 * Behavior from React Aria, skin from the Bloo design system.
 *
 * Over the design project's plain <button> this adds: correct press handling
 * across mouse/touch/keyboard/screen-reader, `data-focus-visible` so focus rings
 * only show for keyboard users, `data-disabled` and `data-pressed` state hooks,
 * and it never fires onPress while disabled.
 *
 * Prop names and their allowed values match `_adherence.oxlintrc.json` exactly,
 * so the design-adherence lint keeps passing.
 */
export function Button({
  variant = 'solid',
  size = 'md',
  tone = 'accent',
  fullWidth,
  className,
  style,
  ...rest
}: ButtonProps) {
  return (
    <AriaButton
      {...rest}
      className={cx(
        'bui-btn',
        `bui-btn-${variant}`,
        size !== 'md' && `bui-btn-${size}`,
        tone !== 'accent' && `bui-tone-${tone}`,
        className,
      )}
      style={fullWidth ? { width: '100%', ...style } : style}
    />
  )
}
